import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  listBookings,
  cancelBooking,
  completeBooking,
  markNoShow,
  retryCalendarSync,
  setMeetingUrl,
  confirmBooking,
  declineBooking,
  ANSWERED_STATUSES,
  BOOKING_STATUS,
  CANCELLED_BY,
  REQUEST_STATUS,
  MAX_CANCELLATION_REASON_LENGTH,
  MAX_DECLINE_REASON_LENGTH,
} from "../../services/api/adminService";
import { ERROR_CODES } from "../../services/errors/ApiError";
import { isCanceledError, isAuthError, isErrorCode, isNotFoundError } from "../../services/errors/errorHandler";
import { formatBusinessDateTime, formatBusinessTime } from "../../services/utils/dateUtils";
import { safeHttpsUrl } from "../../services/utils/safeUrl";
import { PRACTICE, lateCancelFeeText } from "../../data/practice";
import {
  isActive,
  canMarkOutcome,
  canSetMeetingUrl,
  hasStarted,
  insideCancellationWindow,
  lateFeeApplies,
  lateFeeAmount,
  validateMeetingUrl,
  MAX_MEETING_URL_LENGTH,
} from "./bookingRules";
import AdminHeader from "./AdminHeader";
import EmailAlerts from "./EmailAlerts";
import "./admin.css";

/**
 * Tabs. Upcoming/Past split by start time so a booking marked completed
 * early stays where the owner expects it; Cancelled lists by status.
 * Booking requests ('scheduled', contract v1.3) are not in the tabs: they
 * have their own "Waiting for you" list above them until she answers.
 */
export const TABS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
  { id: "cancelled", label: "Cancelled" },
];

const queryForTab = (tab, now) => {
  switch (tab) {
    case "past":
      return { status: ANSWERED_STATUSES, to: now };
    case "cancelled":
      return { status: [BOOKING_STATUS.CANCELLED] };
    case "upcoming":
    default:
      // Soonest first from the server, so the first page (and every
      // "Load more") holds the next sessions, not the furthest-out 100.
      return { status: ANSWERED_STATUSES, from: now, order: "asc" };
  }
};

// Upcoming reads soonest first. The server already returns it that way
// (order=asc); the client sort only keeps merged "Load more" pages stable.
const soonestFirst = (bookings) =>
  [...bookings].sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)) || a.id - b.id);

const sortForTab = (tab, bookings) => (tab === "upcoming" ? soonestFirst(bookings) : bookings);

// Newest start first, as the API lists Past and Cancelled.
const latestFirst = (bookings) =>
  [...bookings].sort((a, b) => String(b.start_time).localeCompare(String(a.start_time)) || b.id - a.id);

/** 'scheduled' is a booking request waiting for the owner (contract v1.3). */
export const STATUS_LABELS = {
  scheduled: "Waiting for you",
  confirmed: "Confirmed",
  completed: "Completed",
  no_show: "No-show",
  cancelled: "Cancelled",
};

const PAYMENT_LABELS = {
  pay_at_session: "Pay at session",
  not_required: "No fee",
  pending: "Payment pending",
  paid: "Paid",
  refunded: "Refunded",
};

const SYNC_LABELS = {
  synced: "Calendar: synced",
  pending: "Calendar: pending",
  failed: "Calendar: failed",
  disabled: "Calendar: off",
};

const EMPTY_MESSAGES = {
  upcoming: "No upcoming bookings.",
  past: "No past bookings yet.",
  cancelled: "No cancelled bookings.",
};

const ACTION_DONE = {
  complete: "Booking marked completed.",
  noShow: "Booking marked as a no-show.",
  cancel: "Booking cancelled. It is now under Cancelled.",
  sync: "Calendar sync retried.",
};

const CANCELLED_BY_LABELS = {
  client: "Client (requested by the client)",
  practice: "Practice",
  owner: "Practice",
  calendar_sync: "Removed in Google Calendar",
};

/** After she answers a request (A.14). */
export const REQUEST_DONE = {
  confirm: "Confirmed. The client has been emailed.",
  decline: "Declined. The client has been emailed.",
};

/** A 409 invalid_state or 404 on confirm/decline: someone or something got there first. */
export const REQUEST_STALE =
  "That request can no longer be changed: it was already answered or cancelled, or its time has passed. The list has been updated.";

/** What to tell the owner after saving a session link. */
export const meetingLinkNotice = (before, after) => {
  if (after && after.meeting_link_sent_at) {
    return before.meeting_url === after.meeting_url && before.meeting_link_sent_at
      ? "Session link unchanged. The client already has it; nothing new was sent."
      : "Session link saved and emailed to the client.";
  }
  return "Session link saved, but it was not emailed (email is off). Send it to the client yourself.";
};

export const formatFee = (amount) =>
  typeof amount === "number" && Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "—";

export const paymentLabel = (status) => PAYMENT_LABELS[status] || (status ? status : PAYMENT_LABELS.pay_at_session);

const clientName = (client) =>
  client ? `${client.first_name || ""} ${client.last_name || ""}`.trim() || "—" : "—";

// Mirrors the backend: completed/no_show have nothing to sync, and a
// cancelled booking only needs a sync when a calendar event exists.
const canRetrySync = (booking) =>
  ["failed", "pending"].includes(booking.calendar_sync_status) &&
  (isActive(booking) || (booking.status === BOOKING_STATUS.CANCELLED && Boolean(booking.google_event_id)));

function BookingDetails({ booking }) {
  const rows = [
    ["Booking reference", booking.booking_reference],
    ["Booking ID", booking.id],
    ["Duration", booking.duration_minutes ? `${booking.duration_minutes} min` : null],
    ["Ends", formatBusinessTime(booking.end_time)],
    ["Meeting link", booking.meeting_url],
    ["Session link emailed", booking.meeting_link_sent_at ? formatBusinessDateTime(booking.meeting_link_sent_at) : null],
    ["Client notes", booking.session_notes],
    ["Therapist notes", booking.therapist_notes],
    ["Cancelled", booking.cancelled_at ? formatBusinessDateTime(booking.cancelled_at) : null],
    ["Cancelled by", booking.cancelled_by ? CANCELLED_BY_LABELS[booking.cancelled_by] || booking.cancelled_by : null],
    ["Cancellation reason", booking.cancellation_reason],
    ["Late cancellation", booking.cancellation_fee_applied ? "Yes (inside the cancellation window)" : null],
    ["Calendar sync error", booking.calendar_sync_error],
    ["Calendar sync attempts", booking.calendar_sync_attempts || null],
  ].filter(([, value]) => value !== null && value !== undefined && value !== "");

  return (
    <dl className="admin-details" id={`booking-details-${booking.id}`}>
      {rows.map(([label, value]) => (
        <div key={label} className="admin-details__row">
          <dt>{label}</dt>
          <dd>
            {label === "Meeting link" && safeHttpsUrl(value) ? (
              // https only: this value comes from the API.
              <a href={safeHttpsUrl(value)} target="_blank" rel="noopener noreferrer">{value}</a>
            ) : (
              String(value)
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ConfirmPanel({ id, title, detail, confirmLabel, danger, busy, onConfirm, onDismiss }) {
  return (
    <div className={`admin-panel${danger ? " admin-panel--danger" : ""}`} role="group" aria-labelledby={id}>
      <p id={id} className="admin-panel__title">{title}</p>
      {detail && <p className="admin-panel__detail">{detail}</p>}
      <div className="admin-actions">
        <button
          type="button"
          className={`admin-btn ${danger ? "admin-btn--danger" : "admin-btn--primary"}`}
          disabled={busy}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
        <button type="button" className="admin-btn" disabled={busy} onClick={onDismiss}>
          Not now
        </button>
      </div>
    </div>
  );
}

function CancelPanel({ booking, busy, onConfirm, onDismiss }) {
  const [reason, setReason] = useState("");
  const [cancelledBy, setCancelledBy] = useState("");
  const fieldId = `cancel-reason-${booking.id}`;
  const hours = PRACTICE.cancellationWindowHours;
  const now = Date.now();
  const late = lateFeeApplies(booking, cancelledBy, now);
  const inside = insideCancellationWindow(booking, now);

  let feeNote = null;
  if (late) {
    feeNote = (
      <p className="admin-warning" data-testid="late-fee-warning">
        Late cancellation: this is less than {hours} hours before the session, so the late-cancellation
        fee applies ({lateCancelFeeText()}, {formatFee(lateFeeAmount(booking))}). It is payable at the
        client&apos;s next session; nothing is charged online.
      </p>
    );
  } else if (cancelledBy === CANCELLED_BY.CLIENT) {
    feeNote = (
      <p className="admin-muted">
        {inside
          ? "No session fee is due for this booking, so there is no late-cancellation fee."
          : `More than ${hours} hours before the session: no late-cancellation fee.`}
      </p>
    );
  } else if (cancelledBy === CANCELLED_BY.PRACTICE) {
    feeNote = <p className="admin-muted">No fee applies when the practice cancels.</p>;
  }

  return (
    <div className="admin-panel admin-panel--danger" role="group" aria-labelledby={`${fieldId}-title`}>
      <p id={`${fieldId}-title`} className="admin-panel__title">
        Cancel this booking? The client is emailed and the calendar event is removed.
      </p>
      <fieldset className="admin-choice">
        <legend>Who asked for the cancellation?</legend>
        {[
          [CANCELLED_BY.CLIENT, "Client requested"],
          [CANCELLED_BY.PRACTICE, "Practice cancelling"],
        ].map(([value, label]) => (
          <label key={value} className="admin-choice__option">
            <input
              type="radio"
              name={`cancelled-by-${booking.id}`}
              value={value}
              checked={cancelledBy === value}
              onChange={() => setCancelledBy(value)}
            />{" "}
            {label}
          </label>
        ))}
      </fieldset>
      <div aria-live="polite">{feeNote}</div>
      <label htmlFor={fieldId}>Reason (optional, kept on the booking)</label>
      <textarea
        id={fieldId}
        rows={2}
        maxLength={MAX_CANCELLATION_REASON_LENGTH}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="admin-actions">
        <button
          type="button"
          className="admin-btn admin-btn--danger"
          disabled={busy || !cancelledBy}
          onClick={() => onConfirm({ cancelledBy, reason })}
        >
          Confirm cancellation
        </button>
        <button type="button" className="admin-btn" disabled={busy} onClick={onDismiss}>
          Keep booking
        </button>
      </div>
    </div>
  );
}

function LinkPanel({ booking, busy, onSave, onDismiss }) {
  const [value, setValue] = useState(safeHttpsUrl(booking.meeting_url) || "");
  const [error, setError] = useState("");
  const inputId = `meeting-url-${booking.id}`;
  const errorId = `${inputId}-error`;

  const submit = (e) => {
    e.preventDefault();
    const result = validateMeetingUrl(value);
    if (result.error) {
      setError(result.error);
      return;
    }
    setError("");
    onSave(result.url);
  };

  return (
    <form className="admin-panel" onSubmit={submit} noValidate aria-labelledby={`${inputId}-title`}>
      <p id={`${inputId}-title`} className="admin-panel__title">Session link</p>
      <p className="admin-panel__detail">
        The client is emailed the link once. Saving the same link again sends nothing.
      </p>
      <label htmlFor={inputId}>Join link (https://)</label>
      <input
        id={inputId}
        type="url"
        inputMode="url"
        autoComplete="off"
        spellCheck={false}
        maxLength={MAX_MEETING_URL_LENGTH}
        placeholder="https://meet.google.com/abc-defg-hij"
        value={value}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        onChange={(e) => setValue(e.target.value)}
      />
      {error && <p id={errorId} className="admin-field-error" role="alert">{error}</p>}
      <div className="admin-actions">
        <button type="submit" className="admin-btn admin-btn--primary" disabled={busy}>
          Save link
        </button>
        <button type="button" className="admin-btn" disabled={busy} onClick={onDismiss}>
          Close
        </button>
      </div>
    </form>
  );
}

function DeclinePanel({ booking, busy, onDecline, onDismiss }) {
  const [reason, setReason] = useState("");
  const fieldId = `decline-reason-${booking.id}`;
  return (
    <div
      id={`request-panel-${booking.id}`}
      className="admin-panel admin-panel--danger"
      role="group"
      aria-labelledby={`${fieldId}-title`}
    >
      <p id={`${fieldId}-title`} className="admin-panel__title">
        Decline this request? The time is freed and the client is emailed.
      </p>
      <label htmlFor={fieldId}>Message to the client (optional)</label>
      <p id={`${fieldId}-hint`} className="admin-panel__detail">
        It goes in the email telling them you couldn&apos;t confirm this time. For example, suggest another day.
      </p>
      <textarea
        id={fieldId}
        rows={3}
        maxLength={MAX_DECLINE_REASON_LENGTH}
        value={reason}
        aria-describedby={`${fieldId}-hint`}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="admin-actions">
        <button type="button" className="admin-btn admin-btn--danger" disabled={busy} onClick={() => onDecline(reason)}>
          Decline request
        </button>
        <button type="button" className="admin-btn" disabled={busy} onClick={onDismiss}>
          Keep it waiting
        </button>
      </div>
    </div>
  );
}

/** One booking request in "Waiting for you": who, what, when, their note, Confirm / Decline. */
function RequestRow({ booking, busy, declining, onConfirm, onDecline, onToggleDecline }) {
  const client = booking.client;
  const titleId = `request-${booking.id}-title`;
  // Confirm is refused once the start time has passed (409); Decline still
  // lets the client know.
  const started = hasStarted(booking, Date.now());

  return (
    <li className="admin-booking admin-request" data-request-id={booking.id}>
      <div className="admin-booking__head">
        <h3 className="admin-booking__time" id={titleId}>
          {formatBusinessDateTime(booking.start_time) || "Time unavailable"}
          <span className="sr-only">, {clientName(client)}</span>
        </h3>
        <span className="admin-badge admin-badge--status-scheduled" data-testid="booking-status">
          {STATUS_LABELS.scheduled}
        </span>
      </div>

      <dl className="admin-fields">
        <div><dt>Client</dt><dd>{clientName(client)}</dd></div>
        <div><dt>Service</dt><dd>{booking.service_name || booking.appointment_type || "—"}</dd></div>
        <div>
          <dt>Email</dt>
          <dd>{client && client.email ? <a href={`mailto:${client.email}`}>{client.email}</a> : "—"}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{client && client.phone ? <a href={`tel:${client.phone}`}>{client.phone}</a> : "—"}</dd>
        </div>
        <div><dt>Fee</dt><dd>{formatFee(booking.fee_amount)}</dd></div>
        {booking.session_notes && (
          <div className="admin-request__note">
            <dt>Their note</dt>
            <dd>{booking.session_notes}</dd>
          </div>
        )}
      </dl>

      {started && (
        <p className="admin-warning">
          This time has passed, so it can&apos;t be confirmed. Decline it to let the client know.
        </p>
      )}

      <div className="admin-actions">
        {!started && (
          <button
            type="button"
            className="admin-btn admin-btn--primary"
            disabled={busy}
            aria-describedby={titleId}
            onClick={() => onConfirm(booking)}
          >
            Confirm
          </button>
        )}
        <button
          type="button"
          className="admin-btn admin-btn--danger"
          disabled={busy}
          aria-describedby={titleId}
          aria-expanded={declining}
          aria-controls={declining ? `request-panel-${booking.id}` : undefined}
          onClick={() => onToggleDecline(booking.id)}
        >
          Decline
        </button>
      </div>

      {declining && (
        <DeclinePanel
          booking={booking}
          busy={busy}
          onDecline={(reason) => onDecline(booking, reason)}
          onDismiss={() => onToggleDecline(booking.id)}
        />
      )}
    </li>
  );
}

function BookingRow({ booking, busy, open, panel, onToggle, onAction, onOpenPanel, onClosePanel }) {
  const client = booking.client;
  const syncStatus = booking.calendar_sync_status;
  const now = Date.now();
  const outcome = canMarkOutcome(booking, now);
  const linkable = canSetMeetingUrl(booking, now);
  const hasLink = Boolean(safeHttpsUrl(booking.meeting_url));
  const panelId = `booking-panel-${booking.id}`;
  const panelButton = (kind, label, extraClass = "") => (
    <button
      type="button"
      className={`admin-btn${extraClass}`}
      disabled={busy}
      aria-expanded={panel === kind}
      aria-controls={panel === kind ? panelId : undefined}
      onClick={() => (panel === kind ? onClosePanel() : onOpenPanel(booking.id, kind))}
    >
      {label}
    </button>
  );

  return (
    <li className="admin-booking" data-booking-id={booking.id}>
      <div className="admin-booking__head">
        <h3 className="admin-booking__time">{formatBusinessDateTime(booking.start_time) || "Time unavailable"}</h3>
        <div className="admin-badges">
          <span className={`admin-badge admin-badge--status-${booking.status}`} data-testid="booking-status">
            {STATUS_LABELS[booking.status] || booking.status}
          </span>
          {syncStatus && (
            <span
              className={`admin-badge admin-badge--sync-${syncStatus}`}
              title={syncStatus === "failed" && booking.calendar_sync_error ? booking.calendar_sync_error : undefined}
            >
              {SYNC_LABELS[syncStatus] || `Calendar: ${syncStatus}`}
            </span>
          )}
        </div>
      </div>

      <dl className="admin-fields">
        <div><dt>Service</dt><dd>{booking.service_name || booking.appointment_type || "—"}</dd></div>
        <div><dt>Client</dt><dd>{clientName(client)}</dd></div>
        <div>
          <dt>Email</dt>
          <dd>{client && client.email ? <a href={`mailto:${client.email}`}>{client.email}</a> : "—"}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{client && client.phone ? <a href={`tel:${client.phone}`}>{client.phone}</a> : "—"}</dd>
        </div>
        <div><dt>Fee</dt><dd>{formatFee(booking.fee_amount)}</dd></div>
        <div><dt>Payment</dt><dd>{paymentLabel(booking.payment_status)}</dd></div>
        {linkable && (
          <div>
            <dt>Session link</dt>
            <dd data-testid="session-link-state">
              {hasLink ? (booking.meeting_link_sent_at ? "Added, emailed" : "Added, not emailed") : "Not added yet"}
            </dd>
          </div>
        )}
      </dl>

      <div className="admin-actions">
        <button
          type="button"
          className="admin-btn"
          aria-expanded={open}
          aria-controls={`booking-details-${booking.id}`}
          onClick={() => onToggle(booking.id)}
        >
          {open ? "Hide details" : "Details"}
        </button>
        {outcome && panelButton("complete", "Complete")}
        {outcome && panelButton("noShow", "No-show")}
        {linkable && panelButton("link", hasLink ? "Change session link" : "Add session link")}
        {isActive(booking) && panelButton("cancel", "Cancel", " admin-btn--danger")}
        {canRetrySync(booking) && (
          <button type="button" className="admin-btn" disabled={busy} onClick={() => onAction("sync", booking)}>
            Retry sync
          </button>
        )}
      </div>

      <div id={panelId}>
        {panel === "complete" && outcome && (
          <ConfirmPanel
            id={`${panelId}-complete`}
            title="Mark this session as completed?"
            detail="Use this once the session has taken place."
            confirmLabel="Yes, mark completed"
            busy={busy}
            onConfirm={() => onAction("complete", booking)}
            onDismiss={onClosePanel}
          />
        )}
        {panel === "noShow" && outcome && (
          <ConfirmPanel
            id={`${panelId}-no-show`}
            title="Mark the client as a no-show?"
            detail="Use this when the client did not attend the session."
            confirmLabel="Yes, mark no-show"
            danger
            busy={busy}
            onConfirm={() => onAction("noShow", booking)}
            onDismiss={onClosePanel}
          />
        )}
        {panel === "link" && linkable && (
          <LinkPanel
            booking={booking}
            busy={busy}
            onSave={(url) => onAction("link", booking, url)}
            onDismiss={onClosePanel}
          />
        )}
        {panel === "cancel" && isActive(booking) && (
          <CancelPanel
            booking={booking}
            busy={busy}
            onConfirm={(options) => onAction("cancel", booking, options)}
            onDismiss={onClosePanel}
          />
        )}
      </div>

      {/* Notes stay out of the DOM until the owner opens the row. */}
      {open && <BookingDetails booking={booking} />}
    </li>
  );
}

export default function AdminBookings() {
  const [tab, setTab] = useState("upcoming");
  const [reloadKey, setReloadKey] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [openIds, setOpenIds] = useState(() => new Set());
  // The one open action panel: {id, kind: 'complete'|'noShow'|'cancel'|'link'}.
  const [panel, setPanel] = useState(null);
  const queryRef = useRef(null);
  // "Waiting for you": booking requests, soonest first (contract v1.3).
  const [requests, setRequests] = useState([]);
  const [requestTotal, setRequestTotal] = useState(null);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsError, setRequestsError] = useState("");
  const [requestNotice, setRequestNotice] = useState("");
  const [requestError, setRequestError] = useState("");
  const [decliningId, setDecliningId] = useState(null);
  const requestsHeadingRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    const query = queryForTab(tab, new Date());
    queryRef.current = query;
    setLoading(true);
    setError("");
    setBookings([]);
    setTotal(0);
    setOpenIds(new Set());
    setPanel(null);

    listBookings({ ...query, signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setBookings(sortForTab(tab, data.bookings));
        setTotal(data.total);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err)) return;
        // A 401 is handled by AdminGuard (redirect to sign in).
        if (!isAuthError(err)) setError(err.message || "Could not load bookings.");
        setLoading(false);
      });

    return () => controller.abort();
  }, [tab, reloadKey]);

  // Requests load once per visit and on Refresh, not on tab changes.
  useEffect(() => {
    const controller = new AbortController();
    setRequestsLoading(true);
    setRequestsError("");
    setDecliningId(null);

    listBookings({ status: [REQUEST_STATUS], order: "asc", signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        const waiting = data.bookings.filter((b) => b.status === REQUEST_STATUS);
        setRequests(soonestFirst(waiting));
        // The server total counts requests; trust it only when every row was one.
        setRequestTotal(waiting.length === data.bookings.length ? data.total : waiting.length);
        setRequestsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err)) return;
        if (!isAuthError(err)) {
          setRequestsError(`Couldn't load your booking requests. ${err.message || ""} Tap Refresh to try again.`.replace(/\s+/g, " "));
        }
        setRequestsLoading(false);
      });

    return () => controller.abort();
  }, [reloadKey]);

  const loadMore = async () => {
    if (loadingMore || !queryRef.current) return;
    setLoadingMore(true);
    try {
      const data = await listBookings({ ...queryRef.current, offset: bookings.length });
      const seen = new Set(bookings.map((b) => b.id));
      setBookings(sortForTab(tab, [...bookings, ...data.bookings.filter((b) => !seen.has(b.id))]));
      setTotal(data.total);
    } catch (err) {
      if (!isAuthError(err)) setActionError(err.message || "Could not load more bookings.");
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleOpen = useCallback((id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openPanel = useCallback((id, kind) => setPanel({ id, kind }), []);
  const closePanel = useCallback(() => setPanel(null), []);

  // `arg`: {cancelledBy, reason} for cancel, the URL for link.
  const runAction = async (action, booking, arg) => {
    if (busyId !== null) return;
    setBusyId(booking.id);
    setNotice("");
    setActionError("");
    try {
      let updated;
      if (action === "complete") updated = await completeBooking(booking.id);
      else if (action === "noShow") updated = await markNoShow(booking.id);
      else if (action === "cancel") updated = await cancelBooking(booking.id, arg);
      else if (action === "link") updated = await setMeetingUrl(booking.id, arg);
      else updated = await retryCalendarSync(booking.id);

      if (action === "cancel" && tab !== "cancelled") {
        setBookings((prev) => prev.filter((b) => b.id !== booking.id));
        setTotal((t) => Math.max(0, t - 1));
      } else if (updated && typeof updated === "object") {
        setBookings((prev) => prev.map((b) => (b.id === booking.id ? { ...b, ...updated } : b)));
      }
      setPanel(null);
      let message = ACTION_DONE[action];
      if (action === "sync" && updated && updated.calendar_sync_status === "failed") {
        message = "Calendar sync failed again. The booking itself is safe; try again later.";
      } else if (action === "cancel" && updated && updated.cancellation_fee_applied) {
        message = "Booking cancelled. The late-cancellation fee applies; it is payable at the client's next session.";
      } else if (action === "link") {
        message = meetingLinkNotice(booking, updated);
      }
      setNotice(message);
    } catch (err) {
      if (!isAuthError(err)) setActionError(err.message || "That didn't work. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const toggleDecline = useCallback((id) => setDecliningId((open) => (open === id ? null : id)), []);

  // Confirm or decline a request (A.14), then update both lists in place.
  const answerRequest = async (action, booking, reason) => {
    if (busyId !== null) return;
    setBusyId(booking.id);
    setRequestNotice("");
    setRequestError("");
    try {
      const updated = action === "confirm" ? await confirmBooking(booking.id) : await declineBooking(booking.id, reason);
      const answered = {
        ...booking,
        ...(updated && typeof updated === "object" ? updated : {}),
      };
      if (!updated || !updated.status) {
        answered.status = action === "confirm" ? BOOKING_STATUS.CONFIRMED : BOOKING_STATUS.CANCELLED;
      }

      setRequests((prev) => prev.filter((b) => b.id !== booking.id));
      setRequestTotal((n) => (typeof n === "number" ? Math.max(0, n - 1) : n));
      setDecliningId(null);
      // Show it in the open tab when it now belongs there.
      if (action === "confirm" && tab === "upcoming" && !hasStarted(answered, Date.now())) {
        setBookings((prev) => soonestFirst([...prev.filter((b) => b.id !== answered.id), answered]));
        setTotal((t) => t + 1);
      } else if (action === "decline" && tab === "cancelled") {
        setBookings((prev) => latestFirst([...prev.filter((b) => b.id !== answered.id), answered]));
        setTotal((t) => t + 1);
      }
      setRequestNotice(REQUEST_DONE[action]);
      // The row is gone: keep keyboard and screen-reader focus in the section.
      if (requestsHeadingRef.current) requestsHeadingRef.current.focus();
    } catch (err) {
      if (isAuthError(err)) return;
      if (isErrorCode(err, ERROR_CODES.INVALID_STATE) || isNotFoundError(err)) {
        setRequestError(REQUEST_STALE);
        setReloadKey((k) => k + 1);
      } else {
        setRequestError(err.message || "That didn't work. Please try again.");
      }
    } finally {
      setBusyId(null);
    }
  };

  const onTabKeyDown = (e) => {
    const index = TABS.findIndex((t) => t.id === tab);
    let nextIndex = null;
    if (e.key === "ArrowRight") nextIndex = (index + 1) % TABS.length;
    if (e.key === "ArrowLeft") nextIndex = (index - 1 + TABS.length) % TABS.length;
    if (nextIndex === null) return;
    e.preventDefault();
    setTab(TABS[nextIndex].id);
    const el = document.getElementById(`admin-tab-${TABS[nextIndex].id}`);
    el && el.focus();
  };

  return (
    <div className="admin">
      {/* null until known: the header then shows no count and asks nothing itself. */}
      <AdminHeader requestCount={requestTotal} />

      <main id="main" className="admin-main" tabIndex={-1}>
        <div className="admin-title-row">
          <h1>Bookings</h1>
          <button
            type="button"
            className="admin-btn"
            onClick={() => {
              setNotice("");
              setActionError("");
              setRequestNotice("");
              setRequestError("");
              setReloadKey((k) => k + 1);
            }}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
        <p className="admin-muted">All times are Bahamas time (America/Nassau).</p>

        <section className="admin-requests" aria-labelledby="requests-heading" aria-busy={requestsLoading}>
          <h2 id="requests-heading" className="admin-requests__title" tabIndex={-1} ref={requestsHeadingRef}>
            Waiting for you
            {requestTotal > 0 && <span className="admin-requests__count"> ({requestTotal})</span>}
          </h2>
          <p className="admin-muted admin-requests__intro">
            Booking requests from the website. Each time is held until you confirm or decline it, and the
            client is emailed either way.
          </p>

          <div aria-live="polite" className="admin-live">
            {requestNotice && <p className="admin-notice">{requestNotice}</p>}
          </div>
          {requestError && <p className="admin-error" role="alert">{requestError}</p>}

          {requestsLoading && <p className="admin-muted" role="status">Loading requests…</p>}
          {!requestsLoading && requestsError && <p className="admin-error" role="alert">{requestsError}</p>}
          {!requestsLoading && !requestsError && requests.length === 0 && (
            <p className="admin-requests__empty">No booking requests waiting.</p>
          )}
          {!requestsLoading && !requestsError && requests.length > 0 && (
            <>
              <ul className="admin-list">
                {requests.map((b) => (
                  <RequestRow
                    key={b.id}
                    booking={b}
                    busy={busyId !== null}
                    declining={decliningId === b.id}
                    onConfirm={(booking) => answerRequest("confirm", booking)}
                    onDecline={(booking, reason) => answerRequest("decline", booking, reason)}
                    onToggleDecline={toggleDecline}
                  />
                ))}
              </ul>
              {requestTotal > requests.length && (
                <p className="admin-muted">
                  Showing the first {requests.length} of {requestTotal}. Answer these to see the rest.
                </p>
              )}
            </>
          )}
        </section>

        <EmailAlerts refreshKey={reloadKey} />

        <div className="admin-tabs" role="tablist" aria-label="Booking lists" onKeyDown={onTabKeyDown}>
          {TABS.map((t) => (
            <button
              key={t.id}
              id={`admin-tab-${t.id}`}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              aria-controls="admin-tabpanel"
              tabIndex={tab === t.id ? 0 : -1}
              className={`admin-tab ${tab === t.id ? "is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div aria-live="polite" className="admin-live">
          {notice && <p className="admin-notice">{notice}</p>}
        </div>
        {actionError && <p className="admin-error" role="alert">{actionError}</p>}

        <section id="admin-tabpanel" role="tabpanel" aria-labelledby={`admin-tab-${tab}`} aria-busy={loading}>
          {loading && <p className="admin-muted" role="status">Loading bookings…</p>}

          {!loading && error && (
            <div className="admin-error" role="alert">
              <p>{error}</p>
              <button type="button" className="admin-btn" onClick={() => setReloadKey((k) => k + 1)}>
                Try again
              </button>
            </div>
          )}

          {!loading && !error && bookings.length === 0 && (
            <p className="admin-empty">{EMPTY_MESSAGES[tab]}</p>
          )}

          {!loading && !error && bookings.length > 0 && (
            <>
              <p className="admin-muted admin-count">
                Showing {bookings.length} of {total}
              </p>
              <ul className="admin-list">
                {bookings.map((b) => (
                  <BookingRow
                    key={b.id}
                    booking={b}
                    busy={busyId !== null}
                    open={openIds.has(b.id)}
                    panel={panel && panel.id === b.id ? panel.kind : null}
                    onToggle={toggleOpen}
                    onAction={runAction}
                    onOpenPanel={openPanel}
                    onClosePanel={closePanel}
                  />
                ))}
              </ul>
              {bookings.length < total && (
                <button type="button" className="admin-btn" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
